













































































































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { getAutocompletion } from '@/api/api';
import { debounce } from 'lodash';

interface TransportLine {
  id: number;
  ref: string;
  name: string;
  route: string;
  colour: string;
  from: string;
  to: string;
  distanceKm: number;
}

interface ModeData {
  loading: boolean;
  lines: TransportLine[];
  showAll: boolean;
  error: string | null;
}

const MODE_LIST = [
  { key: 'subway', label: 'Métro',       icon: 'fa-subway',     cssClass: 'metro',  color: '#003D9B', shadow: 'rgba(0,61,155,0.4)',   sub: 'Lignes souterraines'           },
  { key: 'tram',   label: 'Tramway',     icon: 'fa-train-tram', cssClass: 'tram',   color: '#E95C0A', shadow: 'rgba(233,92,10,0.4)',  sub: 'Lignes de tramway'             },
  { key: 'train',  label: 'Train & RER', icon: 'fa-train',      cssClass: 'train',  color: '#7843A4', shadow: 'rgba(120,67,164,0.4)', sub: 'Transilien, Intercités & RER'  },
  { key: 'bus',    label: 'Bus',         icon: 'fa-bus',        cssClass: 'bus',    color: '#4CAF50', shadow: 'rgba(76,175,80,0.4)',  sub: 'Lignes de bus'                 },
];

const TYPE_COLORS: Record<string, string> = {
  tram: '#FF6B6B', subway: '#4ECDC4', bus: '#96CEB4', train: '#7843A4',
};

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  'https://overpass.openstreetmap.fr/api/interpreter',
];

// Module-level variable — lives only for the current page lifecycle.
// Cleared automatically on page refresh; expires after 2 hours of inactivity.
let _transportCache: { payload: any; savedAt: number } | null = null;
const CACHE_TTL = 2 * 60 * 60 * 1000;

@Component({})
class TransportLinesPopup extends Vue {
  @Prop({ default: false }) visible!: boolean;
  @Prop({ default: null }) initialAddress!: any;

  localVisible: boolean = false;
  step: number = 1;
  maxStep: number = 1;

  address: string = '';
  addressResults: any[] = [];
  addressSelected: any = null;

  radius: number = 1;
  lastFetchedRadius: number = -1;

  modes: Record<string, boolean> = {
    tram: false, subway: false, bus: false, train: false,
  };

  modeData: Record<string, ModeData> = {
    tram:   { loading: false, lines: [], showAll: false, error: null },
    subway: { loading: false, lines: [], showAll: false, error: null },
    bus:    { loading: false, lines: [], showAll: false, error: null },
    train:  { loading: false, lines: [], showAll: false, error: null },
  };

  rawElementsByMode: Record<string, any[]> = {
    tram: [], subway: [], bus: [], train: [],
  };

  selectedLineIds: number[] = [];

  apiCooldown: boolean = false;
  private cooldownTimer: any = null;

  debouncedSearch!: Function;

  // ─── Constants exposed to template ───────────────────────────

  get modeList() { return MODE_LIST; }

  get isApiLocked(): boolean {
    return Object.keys(this.modeData).some(k => this.modeData[k].loading) || this.apiCooldown;
  }

  get stepLabels() { return ['Adresse', 'Distance', 'Transport']; }

  // ─── Lifecycle ───────────────────────────────────────────────

  created() {
    this.localVisible = this.visible;
    this.debouncedSearch = debounce(this.doSearch, 300);
  }

  // ─── Watchers ────────────────────────────────────────────────

  @Watch('visible')
  onVisibleChange(val: boolean) {
    this.localVisible = val;
  }

  @Watch('localVisible')
  onLocalVisibleChange(val: boolean) {
    this.$emit('update:visible', val);
    if (!val) {
      this.reset();
    } else {
      if (this.restoreFromCache(false)) {
        this.step = 3;
        this.maxStep = 3;
      } else if (this.initialAddress) {
        this.address = this.initialAddress.properties.label;
        this.addressSelected = this.initialAddress;
      }
    }
  }

  @Watch('address')
  onAddressChange(val: string) {
    if (!val || val.trim().length < 3) { this.addressResults = []; return; }
    if (this.addressSelected && this.addressSelected.properties.label === val) return;
    this.addressSelected = null;
    this.debouncedSearch();
  }

  // ─── Navigation ──────────────────────────────────────────────

  goNext() {
    if (this.step === 1 && !this.addressSelected) return;
    if (this.step < 3) {
      if (this.step === 2) {
        const radiusChanged = this.radius !== this.lastFetchedRadius;
        if (radiusChanged) {
          this.clearAllModeData();
          this.lastFetchedRadius = this.radius;
        }
        const restored = this.restoreFromCache(true);
        if (!restored && radiusChanged) {
          for (const key of Object.keys(this.modes)) {
            if (this.modes[key]) this.fetchLinesForMode(key);
          }
        }
      }
      this.step++;
      if (this.step > this.maxStep) this.maxStep = this.step;
    }
  }

  goPrev() {
    if (this.step > 1) this.step--;
  }

  tryGoToStep(n: number) {
    if (n <= this.maxStep) this.step = n;
  }

  // ─── Address ─────────────────────────────────────────────────

  async doSearch() {
    try {
      const { data } = await getAutocompletion(this.address.split(' ').join('+'));
      this.addressResults = data.features || [];
    } catch {
      this.addressResults = [];
    }
  }

  onSelectAddress(item: any) {
    this.address = item.properties.label;
    this.addressSelected = item;
    this.addressResults = [];
    this.$emit('address-selected', item);
  }

  // ─── Mode toggles ─────────────────────────────────────────────

  onModeToggle(key: string, isOn: boolean) {
    if (this.isApiLocked && !this.modeData[key].loading) return;
    this.$set(this.modes, key, isOn);
    if (isOn) {
      this.fetchLinesForMode(key);
    } else {
      const lineIds = this.modeData[key].lines.map((l: TransportLine) => l.id);
      this.selectedLineIds = this.selectedLineIds.filter(id => lineIds.indexOf(id) === -1);
      this.$set(this.modeData, key, { loading: false, lines: [], showAll: false, error: null });
      this.rawElementsByMode[key] = [];
      this.saveToCache();
    }
  }

  // ─── Per-mode Overpass fetch ──────────────────────────────────

  async fetchLinesForMode(key: string) {
    if (!this.addressSelected) return;
    const [lon, lat] = this.addressSelected.geometry.coordinates;
    this.$set(this.modeData[key], 'loading', true);
    this.$set(this.modeData[key], 'lines', []);
    this.$set(this.modeData[key], 'showAll', false);
    this.$set(this.modeData[key], 'error', null);
    this.rawElementsByMode[key] = [];

    const query = this.getModeQuery(key, lat, lon);

    const isRateLimitText = (t: string) =>
      t.includes('rate_limited') || t.toLowerCase().includes('too many requests');

    const isError = (t: string) =>
      t.includes('rate_limited') || t.includes('timeout') ||
      t.includes('open64') || t.trimStart().startsWith('<?xml');

    try {
      let data: any = null;
      let gotRateLimit = false;
      for (const ep of OVERPASS_ENDPOINTS) {
        const controller = new AbortController();
        const timerId = setTimeout(() => controller.abort(), 10000);
        try {
          const r = await fetch(ep, { method: 'POST', body: query, signal: controller.signal });
          clearTimeout(timerId);
          if (!r.ok) {
            if (r.status === 429) gotRateLimit = true;
            continue;
          }
          const text = await r.text();
          if (isError(text)) {
            if (isRateLimitText(text)) gotRateLimit = true;
            continue;
          }
          data = JSON.parse(text);
          break;
        } catch (fetchErr) {
          clearTimeout(timerId);
          /* try next endpoint */
        }
      }

      if (!data || !data.elements) {
        const msg = gotRateLimit
          ? 'Trop de requêtes envoyées au serveur. Veuillez patienter 1 à 2 minutes puis cliquez sur Réessayer.'
          : 'Impossible de charger les lignes. Veuillez réessayer.';
        this.$set(this.modeData[key], 'error', msg);
        this.$set(this.modeData[key], 'loading', false);
        return;
      }

      // Build node tag map from stop-node elements returned by the tags query
      const nodeTagMap: Record<number, any> = {};
      for (const el of data.elements) {
        if (el.type === 'node' && el.tags) {
          nodeTagMap[el.id] = el.tags;
        }
      }
      // Attach tags to each relation's member node objects so the name travels with the relation
      for (const el of data.elements) {
        if (el.type === 'relation' && el.members) {
          for (const m of el.members) {
            if (m.type === 'node' && nodeTagMap[m.ref]) {
              m.nodeTags = nodeTagMap[m.ref];
            }
          }
        }
      }

      this.rawElementsByMode[key] = data.elements;

      const lines: TransportLine[] = data.elements
        .filter((el: any) => el.type === 'relation' && el.tags)
        .map((el: any) => {
          const t = el.tags;
          let route: string = t.route || '';
          if (t.network === 'RER' || t.network_ref === 'RER') route = 'RER';

          let from = t.from || '';
          let to = t.to || '';
          if ((!from || !to) && el.members) {
            const namedNodes = (el.members as any[]).filter((m: any) =>
              m.type === 'node' && m.nodeTags && m.nodeTags.name
            );
            if (!from && namedNodes.length > 0) from = namedNodes[0].nodeTags.name;
            if (!to && namedNodes.length > 1) to = namedNodes[namedNodes.length - 1].nodeTags.name;
          }

          return {
            id: el.id,
            ref: t.ref || '',
            name: t.name || t.ref || 'Ligne inconnue',
            route,
            colour: t.colour || '',
            from,
            to,
            distanceKm: this.minDistToAddress(el, lat, lon),
          };
        })
        .filter((line: TransportLine, i: number, arr: TransportLine[]) =>
          arr.findIndex((l: TransportLine) => l.id === line.id) === i
        );

      lines.sort((a: TransportLine, b: TransportLine) => a.distanceKm - b.distanceKm);

      // Pass 1 — bidirectional dedup: Ligne X: A→B and Ligne X: B→A are the same route.
      const seenPairs = new Set<string>();
      const biDedupedLines = lines.filter((line: TransportLine) => {
        const endpoints = [line.from || '', line.to || ''].sort();
        const pairKey = (line.ref || String(line.id)) + '|' + endpoints[0] + '|' + endpoints[1];
        if (seenPairs.has(pairKey)) return false;
        seenPairs.add(pairKey);
        return true;
      });

      // Pass 2 — ref dedup: collapse service-variant duplicates into one entry per line number
      // (e.g. RER D ROPO / ZACO / RUVE → single "RER D"; bus line 91A / 91B → single "91").
      // Lines without a ref are always kept.
      const seenRefs = new Set<string>();
      const finalLines = biDedupedLines.filter((line: TransportLine) => {
        if (!line.ref) return true;
        if (seenRefs.has(line.ref)) return false;
        seenRefs.add(line.ref);
        return true;
      });

      this.$set(this.modeData[key], 'lines', finalLines);
      // Pre-select all
      const newIds = finalLines.map((l: TransportLine) => l.id);
      this.selectedLineIds = [...this.selectedLineIds, ...newIds.filter((id: number) => this.selectedLineIds.indexOf(id) === -1)];
      this.saveToCache();
    } catch (e) {
      console.error('fetchLinesForMode error', key, e);
    } finally {
      this.$set(this.modeData[key], 'loading', false);
      if (!Object.keys(this.modeData).some(k => k !== key && this.modeData[k].loading)) {
        this.apiCooldown = true;
        if (this.cooldownTimer) clearTimeout(this.cooldownTimer);
        this.cooldownTimer = setTimeout(() => {
          this.apiCooldown = false;
          this.cooldownTimer = null;
        }, 1000);
      }
    }
  }

  getModeQuery(key: string, lat: number, lon: number): string {
    const meters = this.radius * 1000;
    let relFilter = '';
    if (key === 'tram') {
      relFilter = `relation["route"="tram"](around:${meters},${lat},${lon})`;
    } else if (key === 'subway') {
      relFilter = `relation["route"="subway"](around:${meters},${lat},${lon})`;
    } else if (key === 'bus') {
      const busM = Math.min(this.radius, 1) * 1000;
      relFilter = `relation["route"="bus"](around:${busM},${lat},${lon})`;
    } else if (key === 'train') {
      const trainM = Math.min(this.radius, 2) * 1000;
      relFilter = `relation["route"="train"]["network"!~"TGV|Thalys|Eurostar|Intercités"](around:${trainM},${lat},${lon});\n` +
        `  relation["network"~"RER"]["route"~"subway|rail"](around:${trainM},${lat},${lon})`;
    }
    // Fetch relations with geometry, then also fetch stop-node tags (for station names)
    return '[out:json][timeout:180][maxsize:16777216];\n' +
      '(' + relFilter + ';)->.rels;\n' +
      '.rels out geom;\n' +
      '(\n' +
      '  node(r.rels:"stop");\n' +
      '  node(r.rels:"stop_entry_only");\n' +
      '  node(r.rels:"stop_exit_only");\n' +
      '  node(r.rels:"platform");\n' +
      '  node(r.rels:"platform_entry_only");\n' +
      '  node(r.rels:"platform_exit_only");\n' +
      ');\n' +
      'out tags qt;';
  }

  // ─── Line helpers ─────────────────────────────────────────────

  visibleLines(key: string): TransportLine[] {
    const lines = this.modeData[key].lines;
    return this.modeData[key].showAll ? lines : lines.slice(0, 5);
  }

  selectedCountForMode(key: string): number {
    const ids = this.modeData[key].lines.map((l: TransportLine) => l.id);
    return this.selectedLineIds.filter(id => ids.indexOf(id) !== -1).length;
  }

  toggleSelectAllMode(key: string) {
    const ids = this.modeData[key].lines.map((l: TransportLine) => l.id);
    const allSelected = ids.every((id: number) => this.selectedLineIds.indexOf(id) !== -1);
    if (allSelected) {
      this.selectedLineIds = this.selectedLineIds.filter(id => ids.indexOf(id) === -1);
    } else {
      this.selectedLineIds = [...this.selectedLineIds, ...ids.filter((id: number) => this.selectedLineIds.indexOf(id) === -1)];
    }
    this.saveToCache();
  }

  toggleLine(id: number) {
    const idx = this.selectedLineIds.indexOf(id);
    if (idx === -1) this.selectedLineIds.push(id);
    else this.selectedLineIds.splice(idx, 1);
    this.saveToCache();
  }

  formatDist(km: number): string {
    if (!isFinite(km)) return '';
    if (km < 1) return Math.round(km * 1000) + ' m';
    return km.toFixed(1) + ' km';
  }

  haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const a = sinDLat * sinDLat +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * sinDLon * sinDLon;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  minDistToAddress(el: any, lat: number, lon: number): number {
    let min = Infinity;
    for (const m of (el && el.members ? el.members : [])) {
      if (m.type === 'node' && m.lat != null) {
        const d = this.haversineKm(lat, lon, m.lat, m.lon);
        if (d < min) min = d;
      } else if (m.type === 'way' && m.geometry) {
        for (const pt of m.geometry) {
          const d = this.haversineKm(lat, lon, pt.lat, pt.lon);
          if (d < min) min = d;
        }
      }
    }
    return min;
  }

  resolveLineColor(line: TransportLine, key: string): string {
    if (line.colour && line.colour.startsWith('#')) return line.colour;
    return TYPE_COLORS[key] || '#999';
  }

  clearAllModeData() {
    for (const key of Object.keys(this.modeData)) {
      this.$set(this.modeData, key, { loading: false, lines: [], showAll: false, error: null });
      this.rawElementsByMode[key] = [];
    }
    this.selectedLineIds = [];
  }

  // ─── Apply / Clear ────────────────────────────────────────────

  applySelection() {
    if (!this.selectedLineIds.length || !this.addressSelected) return;
    const [lon, lat] = this.addressSelected.geometry.coordinates;

    const allElements: any[] = [];
    for (const key of Object.keys(this.rawElementsByMode)) {
      for (const el of this.rawElementsByMode[key]) {
        if (el.type === 'relation' && this.selectedLineIds.indexOf(el.id) !== -1) {
          if (!allElements.find((e: any) => e.id === el.id)) {
            allElements.push(el);
          }
        }
      }
    }

    this.saveToCache();
    this.$emit('apply', {
      elements: allElements,
      lat,
      lon,
      radius: this.radius,
      modes: { ...this.modes },
    });
    this.localVisible = false;
  }

  handleClear() {
    this.$emit('clear');
    this.clearCache();
    this.reset();
    this.localVisible = false;
  }

  private reset() {
    this.step = 1;
    this.maxStep = 1;
    this.address = '';
    this.addressSelected = null;
    this.addressResults = [];
    this.radius = 1;
    this.lastFetchedRadius = -1;
    this.modes = { tram: false, subway: false, bus: false, train: false };
    this.selectedLineIds = [];
    this.apiCooldown = false;
    if (this.cooldownTimer) { clearTimeout(this.cooldownTimer); this.cooldownTimer = null; }
    this.clearAllModeData();
  }

  // ─── Cache helpers ────────────────────────────────────────────
  // Single fixed key stores the last complete search so the popup
  // can reopen at step 3 with the previous selection intact.

  private saveToCache() {
    if (!this.addressSelected) return;
    const modeDataSnapshot: Record<string, any> = {};
    for (const k of Object.keys(this.modeData)) {
      const v = this.modeData[k];
      modeDataSnapshot[k] = { lines: v.lines, showAll: false, error: null };
    }
    _transportCache = {
      savedAt: Date.now(),
      payload: {
        addressLabel: this.addressSelected.properties.label,
        addressSelected: this.addressSelected,
        address: this.address,
        radius: this.radius,
        modes: { ...this.modes },
        modeData: modeDataSnapshot,
        rawElementsByMode: { ...this.rawElementsByMode },
        selectedLineIds: [...this.selectedLineIds],
      },
    };
  }

  // matchParams=true: only restore if saved address+radius match current values (used in goNext).
  // matchParams=false: restore everything including address+radius (used on popup open).
  private restoreFromCache(matchParams: boolean): boolean {
    if (!_transportCache) return false;
    if (Date.now() - _transportCache.savedAt > CACHE_TTL) {
      _transportCache = null;
      return false;
    }
    const payload = _transportCache.payload;
    if (matchParams) {
      if (!this.addressSelected) return false;
      if (payload.addressLabel !== this.addressSelected.properties.label) return false;
      if (payload.radius !== this.radius) return false;
    }
    this.address = payload.address || '';
    this.addressSelected = payload.addressSelected || null;
    this.radius = payload.radius || 1;
    this.lastFetchedRadius = payload.radius || 1;
    if (payload.modes) this.modes = payload.modes;
    for (const k of Object.keys(payload.modeData || {})) {
      this.$set(this.modeData, k, { ...payload.modeData[k], loading: false, error: null });
    }
    if (payload.rawElementsByMode) this.rawElementsByMode = payload.rawElementsByMode;
    if (payload.selectedLineIds) this.selectedLineIds = payload.selectedLineIds;
    return true;
  }

  private clearCache() {
    _transportCache = null;
  }
}

export default TransportLinesPopup;
